/*
 * Invoices, payments, quotes, leads and the public pay and quote pages as React Query hooks (draft 3). Money changes send an
 * idempotency key made once per click, so a double click or a retry never pays or charges twice.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  HeardFrom,
  InvoiceDetail,
  InvoiceFilter,
  InvoiceRow,
  InvoiceState,
  InvoiceSummary,
  LeadRow,
  MoneyToDo,
  PaymentDetail,
  PaymentList,
  ProposalDetail,
  ProposalRow,
  proposalSaveBody,
  Pipeline,
  PublicInvoice,
  PublicQuote,
  QuoteDetail,
  QuoteRow,
  QuoteStats,
  StageRules,
  RequestDetail,
  RequestList,
  RequestRow,
  SendPreview,
  invoiceLineInput,
  leadInput,
  quoteInput,
  quotePatch,
} from "@steward/shared";
import type { z } from "zod";
import { api } from "./client";
import { qs } from "./hooks";

export type InvoiceLineIn = z.input<typeof invoiceLineInput>;
export type QuoteIn = z.input<typeof quoteInput>;
export type QuotePatchIn = z.input<typeof quotePatch>;
export type LeadIn = z.input<typeof leadInput>;
export type Via = "text" | "email";

/** A fresh key for one money action. Kept by the caller until the action is done, so a retry reuses it. */
export const newKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${String(Date.now())}-${String(Math.random()).slice(2)}`;

export interface InvoiceList {
  invoices: InvoiceRow[];
  counts: Record<InvoiceState, number>;
  summary: InvoiceSummary;
  nextCursor: string | null;
}

export const useInvoices = (
  companyId: string,
  q: { state: InvoiceFilter | "all"; q?: string | undefined; clientId?: string; jobId?: string; from?: string; to?: string },
  enabled = true,
) =>
  useQuery({
    queryKey: ["invoices", companyId, q],
    queryFn: () => api<InvoiceList>("GET", `invoices${qs({ ...q, limit: 200 })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
/** How many invoices wait in To check: the red count on Invoices, like unread email (the old Steward had it). */
export const useToCheckCount = (enabled: boolean) =>
  useQuery({
    queryKey: ["invoices", "to-check-count"],
    queryFn: () => api<{ count: number }>("GET", "invoices/to-check-count"),
    refetchInterval: 60_000,
    enabled,
  });
export const useInvoice = (id: string) =>
  useQuery({ queryKey: ["invoice", id], queryFn: () => api<InvoiceDetail>("GET", `invoices/${id}`), enabled: id !== "" });
export const useSendPreview = (id: string, via: Via, enabled: boolean) =>
  useQuery({
    queryKey: ["invoice-send", id, via],
    queryFn: () => api<SendPreview>("GET", `invoices/${id}/send${qs({ via })}`),
    enabled,
  });
export const usePayments = (
  companyId: string,
  q: { from?: string; to?: string; method?: string; status?: string; q?: string; clientId?: string; jobId?: string } = {},
  enabled = true,
) =>
  useQuery({
    queryKey: ["payments", companyId, q],
    queryFn: () => api<PaymentList>("GET", `payments${qs(q)}`),
    placeholderData: keepPreviousData,
    enabled,
  });
export const usePayment = (id: string) =>
  useQuery({ queryKey: ["payment", id], queryFn: () => api<PaymentDetail>("GET", `payments/${id}`) });
export const useMoneyToDo = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["money-todo", companyId], queryFn: () => api<MoneyToDo>("GET", "home/money"), enabled });

/** One client's requests and quotes, for the client page's Work overview. */
export const useClientRequests = (companyId: string, clientId: string, enabled = true) =>
  useQuery({
    queryKey: ["requests", companyId, { clientId }],
    queryFn: () => api<RequestList>("GET", `requests${qs({ clientId })}`),
    enabled,
  });
export const useClientQuotes = (companyId: string, clientId: string, enabled = true) =>
  useQuery({
    queryKey: ["quotes", companyId, { clientId }],
    queryFn: () => api<{ quotes: QuoteRow[] }>("GET", `quotes${qs({ clientId, limit: 200 })}`),
    enabled,
  });
/** Quotes by status. offers "only" lists the plan offers instead (Quotes, Plan offers). */
export const useQuotes = (companyId: string, status: string, enabled = true, offers?: "only") =>
  useQuery({
    queryKey: ["quotes", companyId, status, offers ?? "hide"],
    queryFn: () => api<{ quotes: QuoteRow[] }>("GET", `quotes${qs({ status, limit: 200, offers })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useQuote = (id: string, enabled = true) =>
  useQuery({ queryKey: ["quote", id], queryFn: () => api<QuoteDetail>("GET", `quotes/${id}`), enabled: enabled && Boolean(id) });
/** The board. older shows the leads each column's hide rule keeps off. */
export const usePipeline = (companyId: string, older = false) =>
  useQuery({
    queryKey: ["pipeline", companyId, older],
    queryFn: () => api<Pipeline>("GET", older ? "pipeline?older=1" : "pipeline"),
    placeholderData: keepPreviousData,
  });
export const useQuoteStats = (companyId: string) =>
  useQuery({ queryKey: ["quotes", companyId, "stats"], queryFn: () => api<QuoteStats>("GET", "quotes/stats") });

/** What a money change can show up on. */
const MONEY = [
  "invoices",
  "invoice",
  "invoice-send",
  "payments",
  "payment",
  "money-todo",
  "client",
  "clients",
  "home",
  "quotes",
  "quote",
  "pipeline",
  "requests",
  "request",
  "proposals",
  "proposal",
  // Money in, a tip or who worked a hand-typed invoice all change pay.
  "payroll",
  "mypay",
];

function useMoney<I, O>(fn: (input: I) => Promise<O>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(MONEY.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

export const useCreateInvoice = () =>
  useMoney((v: { clientId: string; lines: InvoiceLineIn[] }) => api<InvoiceDetail>("POST", "invoices", v));
export const useUpdateInvoice = () =>
  useMoney(
    (v: {
      id: string;
      patch: {
        lines?: InvoiceLineIn[];
        poNumber?: string | null;
        noteToClient?: string | null;
        debt?: boolean;
        checkMailed?: boolean;
        crew?: string[];
        nobodyWorked?: boolean;
        payWays?: { card: true; bank: boolean; klarna: boolean } | null;
      };
    }) => api<InvoiceDetail>("PATCH", `invoices/${v.id}`, v.patch),
  );
export const useEditPaid = () =>
  useMoney((v: { id: string; body: { lines: InvoiceLineIn[]; choice?: "credit" | "no_credit" } }) =>
    api<InvoiceDetail>("POST", `invoices/${v.id}/edit-paid`, v.body),
  );
export const useSendInvoice = () =>
  useMoney((v: { id: string; via: Via; to: string; sendAt?: string; cc?: string[]; copyMe?: boolean; pdf?: boolean }) =>
    api<InvoiceDetail>("POST", `invoices/${v.id}/send`, {
      via: v.via,
      to: v.to,
      ...(v.sendAt ? { sendAt: v.sendAt } : {}),
      ...(v.via === "email" ? { cc: v.cc ?? [], copyMe: v.copyMe ?? false, ...(v.pdf !== undefined ? { pdf: v.pdf } : {}) } : {}),
    }),
  );
/** A receipt for a paid invoice, sent by hand: a text, or an email with the PDF. */
export const useSendReceipt = () =>
  useMoney((v: { id: string; via: Via; to: string }) =>
    api<InvoiceDetail>("POST", `invoices/${v.id}/receipt`, { via: v.via, to: v.to }),
  );
/** Bill part of a one-time job now (a progress payment), on an invoice of its own in To check. */
export const useBillProgress = () =>
  useMoney((v: { ref: string; amountCents: number }) =>
    api<InvoiceDetail>("POST", `visits/${v.ref}/progress`, { amountCents: v.amountCents }),
  );
/** A link for the client to save a card on our own page (/card/<token>); texted to them when send is on. */
export const useCardLink = () =>
  useMoney((v: { clientId: string; send: boolean }) =>
    api<{ url: string; sent: boolean }>("POST", `clients/${v.clientId}/card-link`, { send: v.send }),
  );
export const useRefund = () =>
  useMoney((v: { id: string; paymentId: string; amountCents: number; reason: string; keepOwed: boolean }) =>
    api<InvoiceDetail>("POST", `invoices/${v.id}/refund`, {
      paymentId: v.paymentId,
      amountCents: v.amountCents,
      reason: v.reason,
      keepOwed: v.keepOwed,
    }),
  );
export const useNewInvoice = () =>
  useMoney((v: { clientId: string; lines: InvoiceLineIn[]; noteToClient?: string | null; crew?: string[] }) =>
    api<InvoiceDetail>("POST", "invoices", v),
  );
/** Puts invoices of one client on one (a sent one not paid yet is kept, else the oldest); answers with the kept one. */
export const useMergeInvoices = () => useMoney((ids: string[]) => api<InvoiceDetail>("POST", "invoices/merge", { ids }));
/** Takes one visit off an invoice onto a new one in To check; answers with the new one. */
export const useTakeVisitOff = () =>
  useMoney((v: { id: string; visitId: string }) =>
    api<InvoiceDetail>("POST", `invoices/${v.id}/take-off`, { visitId: v.visitId }),
  );
export const useNoCharge = () => useMoney((id: string) => api<InvoiceDetail>("POST", `invoices/${id}/no-charge`));
export const useMarkSent = () => useMoney((id: string) => api<InvoiceDetail>("POST", `invoices/${id}/mark-sent`));
export const useVoidInvoice = () => useMoney((id: string) => api<InvoiceDetail>("POST", `invoices/${id}/void`));
export const useRecordPayment = () =>
  useMoney(
    (v: {
      id: string;
      key: string;
      method: "cash" | "check";
      amountCents: number;
      checkNumber?: string | null;
      receivedOn?: string;
    }) =>
      api<InvoiceDetail>(
        "POST",
        `invoices/${v.id}/payments`,
        {
          method: v.method,
          amountCents: v.amountCents,
          checkNumber: v.checkNumber ?? null,
          ...(v.receivedOn ? { receivedOn: v.receivedOn } : {}),
        },
        { "idempotency-key": v.key },
      ),
  );
export const useChargeCard = () =>
  useMoney((v: { id: string; key: string; amountCents: number; last4: string }) =>
    api<InvoiceDetail>(
      "POST",
      `invoices/${v.id}/charge`,
      { amountCents: v.amountCents, last4: v.last4, confirm: true },
      { "idempotency-key": v.key },
    ),
  );

/** Hold keeps an invoice from charging the saved card by itself; taking it off charges it within a minute. */
export const useAutoChargeHold = () =>
  useMoney((v: { id: string; hold: boolean }) => api<InvoiceDetail>("POST", `invoices/${v.id}/auto-charge`, { hold: v.hold }));

export const useCreateQuote = () => useMoney((v: QuoteIn) => api<QuoteDetail>("POST", "quotes", v));
export const useUpdateQuote = () =>
  useMoney((v: { id: string; patch: QuotePatchIn }) => api<QuoteDetail>("PATCH", `quotes/${v.id}`, v.patch));
export const useSendQuote = () =>
  useMoney((v: { id: string; via: Via; to: string; message?: string }) =>
    api<QuoteDetail>("POST", `quotes/${v.id}/send`, { via: v.via, to: v.to, ...(v.message ? { message: v.message } : {}) }),
  );
export const useDeclineQuote = () => useMoney((id: string) => api<QuoteDetail>("POST", `quotes/${id}/decline`));
export const useArchiveQuote = () => useMoney((id: string) => api<{ ok: true }>("POST", `quotes/${id}/archive`));
export const useRestoreQuote = () => useMoney((id: string) => api<{ ok: true }>("POST", `quotes/${id}/restore`));
export const useStageRules = () =>
  useMoney((v: { id: string; rules: StageRules }) => api<{ ok: true }>("PATCH", `pipeline/stages/${v.id}`, v.rules));
export const useCreateLead = () => useMoney((v: LeadIn) => api<LeadRow>("POST", "leads", v));
/** How they heard about us, picked by the office on the request (HEARD_FROM, or null to clear). */
export const useHeardFrom = () =>
  useMoney((v: { id: string; heardFrom: HeardFrom | null }) =>
    api<LeadRow>("PATCH", `leads/${v.id}`, { heardFrom: v.heardFrom }),
  );
export const useMoveLead = () =>
  useMoney((v: { id: string; stageId: string }) => api<LeadRow>("PATCH", `leads/${v.id}`, { stageId: v.stageId }));

// Public pages (no sign in) -----------------------------------------------------------------------------------------

export const usePublicInvoice = (token: string) =>
  useQuery({ queryKey: ["pay", token], queryFn: () => api<PublicInvoice>("GET", `public/pay/${token}`), retry: false });
/** The client's quote page; previewId opens it signed in for the office, drafts too (api quotes/:id/preview). */
export const usePublicQuote = (token: string, previewId?: string) =>
  useQuery({
    queryKey: previewId ? ["quote-preview", previewId] : ["public-quote", token],
    queryFn: () => api<PublicQuote>("GET", previewId ? `quotes/${previewId}/preview` : `public/quote/${token}`),
    retry: false,
  });
export interface PayStart {
  intentId: string;
  clientSecret: string;
  publishableKey: string | null;
  fake: boolean;
}
export const startPay = (
  token: string,
  tipCents: number,
  saveCard: boolean,
  way?: "card" | "bank" | "klarna",
  autoCharge = false,
) => api<PayStart>("POST", `public/pay/${token}/start`, { tipCents, saveCard, autoCharge, ...(way ? { way } : {}) });
/** A tip after paying (up to 14 days): its own payment, taken on the same page. */
export const startTip = (token: string, tipCents: number) => api<PayStart>("POST", `public/pay/${token}/tip`, { tipCents });
/** Pay with the card saved before: the balance and card's last 4 the page showed, and the tip picked. */
export const paySaved = (token: string, tipCents: number, amountCents: number, last4: string, autoCharge = false) =>
  api<{ paid: boolean; failure: string | null }>("POST", `public/pay/${token}/saved`, {
    tipCents,
    amountCents,
    last4,
    autoCharge,
  });
export const finishPay = (token: string, paymentIntent: string) =>
  api<{ paid: boolean }>("POST", `public/pay/${token}/done`, { paymentIntent });
/** The test card, when Stripe is not connected (a PC, the preview). */
export const testPay = (token: string, paymentIntent: string) =>
  api<{ paid: boolean }>("POST", `public/pay/${token}/test-pay`, { paymentIntent });
export const approveQuote = (token: string, picked: string[], signature: string, drawing: string | null, autoPay = false) =>
  api<PublicQuote>("POST", `public/quote/${token}/approve`, { picked, signature, drawing, autoPay });

// Requests (WORK > Requests) -----------------------------------------------------------------------------------------

export const useRequests = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["requests", companyId], queryFn: () => api<RequestList>("GET", "requests"), enabled });
export const useRequest = (id: string, enabled = true) =>
  useQuery({
    queryKey: ["request", id],
    queryFn: () => api<RequestDetail>("GET", `requests/${id}`),
    enabled: enabled && Boolean(id),
  });
/** Archive, restore, or turn down the work. */
export const useRequestAction = () =>
  useMoney((v: { id: string; act: "archive" | "restore" | "decline" }) =>
    api<RequestRow>("POST", `requests/${v.id}/${v.act}`, {}),
  );

// Proposals (WORK > Proposals) ---------------------------------------------------------------------------------------

export type ProposalIn = z.input<typeof proposalSaveBody>;
export const useProposals = (companyId: string) =>
  useQuery({
    queryKey: ["proposals", companyId],
    queryFn: () => api<{ proposals: ProposalRow[] }>("GET", "proposals"),
  });
export const useProposal = (id: string, enabled = true) =>
  useQuery({ queryKey: ["proposal", id], queryFn: () => api<ProposalDetail>("GET", `proposals/${id}`), enabled });
/** Save a new proposal, or a draft one again. Autosave answers saved false when it has nothing to keep yet. */
export const saveProposal = (id: string | null, body: ProposalIn) =>
  api<{ quoteId: string | null; saved: boolean }>(id ? "PUT" : "POST", id ? `proposals/${id}` : "proposals", body);
